/**
 * Festive Gift Guide
 *
 * Two progressive enhancements for the `festive-*` sections:
 *   1. Reveal-on-scroll for anything marked `.fgg-reveal`.
 *   2. Scoping the add-to-bag loader to the button that was actually clicked.
 *
 * Loaded with `defer`, and only on templates using these sections.
 *
 * Every festive section renders the `festive-styles` snippet, so this file can
 * appear as several <script> tags on one page. Duplicate tags share a single
 * network fetch but each one still EXECUTES, so the guard below is what keeps
 * this to one set of listeners and observers.
 *
 * Without JS the content stays visible: a <noscript> rule in `festive-styles`
 * neutralises the reveal animation, and the loader is purely additive.
 */
(() => {
  'use strict';

  const NAMESPACE = '__festiveGiftGuide';
  if (window[NAMESPACE]) return;
  window[NAMESPACE] = true;

  const prefersReducedMotion = () =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

  /* ------------------------------------------------------------------ *
   * 1. Reveal on scroll
   * ------------------------------------------------------------------ */

  const REVEAL_SELECTOR = '.fgg-reveal';
  const VISIBLE_CLASS = 'is-visible';
  const REVEAL_OPTIONS = { threshold: 0.12, rootMargin: '0px 0px -60px 0px' };

  const show = (el) => el.classList.add(VISIBLE_CLASS);

  /**
   * One observer for the whole page, reused across theme-editor re-renders.
   * Creating one per init call would leak an observer on every section reload.
   */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver((entries, observer) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            show(entry.target);
            observer.unobserve(entry.target);
          }
        }, REVEAL_OPTIONS)
      : null;

  const initReveal = (root = document) => {
    const targets = root.querySelectorAll(`${REVEAL_SELECTOR}:not(.${VISIBLE_CLASS})`);
    if (!targets.length) return;

    // No observer support, or the visitor asked for less motion: skip the
    // animation entirely rather than observing elements CSS already reveals.
    if (!revealObserver || prefersReducedMotion()) {
      targets.forEach(show);
      return;
    }

    targets.forEach((target) => revealObserver.observe(target));
  };

  /** An observer holds a strong reference, so drop targets the editor removes. */
  const teardownReveal = (root) => {
    revealObserver &&
      root.querySelectorAll(REVEAL_SELECTOR).forEach((target) => revealObserver.unobserve(target));
  };

  /* ------------------------------------------------------------------ *
   * 2. Add-to-bag loader
   * ------------------------------------------------------------------ */

  const BUTTON_SELECTOR = '.fgg-card .add-to-cart-btn';
  const LOADING_CLASS = 'fgg-is-loading';
  const THEME_LOADING_CLASS = 'btnLoading';
  const SAFETY_TIMEOUT_MS = 10_000;

  /**
   * The theme's button toggles `btnLoading` from a GLOBAL store flag
   * ($store.cart.isUpdating), so every add-to-cart button on the page switches
   * on together. Tagging the clicked button lets the CSS — which requires both
   * classes — light up that card alone.
   */
  const trackLoading = (button) => {
    let sawThemeLoading = false;
    let settled = false;
    let timer;
    let observer;

    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      observer?.disconnect();
      button.classList.remove(LOADING_CLASS);
    };

    // Never leave a button stuck spinning if the request stalls or errors.
    timer = setTimeout(finish, SAFETY_TIMEOUT_MS);

    if (!('MutationObserver' in window)) return;

    observer = new MutationObserver(() => {
      if (button.classList.contains(THEME_LOADING_CLASS)) {
        sawThemeLoading = true;
        return;
      }
      // Only stand down once the theme's own loading state has been AND gone —
      // otherwise an unrelated class change clears us before it even starts.
      if (sawThemeLoading) finish();
    });

    observer.observe(button, { attributes: true, attributeFilter: ['class'] });
  };

  const onButtonClick = (event) => {
    const button = event.target?.closest?.(BUTTON_SELECTOR);
    if (!button || button.disabled || button.classList.contains(LOADING_CLASS)) return;

    button.classList.add(LOADING_CLASS);
    trackLoading(button);
  };

  /* ------------------------------------------------------------------ *
   * Bootstrap
   * ------------------------------------------------------------------ */

  const onReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
      return;
    }
    callback();
  };

  onReady(() => initReveal());

  // Capture phase: tag the button before Alpine's own @click handler runs.
  document.addEventListener('click', onButtonClick, true);

  // Theme editor: re-scan on re-render, release observed nodes on removal.
  document.addEventListener('shopify:section:load', ({ target }) => initReveal(target));
  document.addEventListener('shopify:section:unload', ({ target }) => teardownReveal(target));
})();
